import React, { useReducer, useState } from 'react';
import { Icon } from '../Icon/Icon';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { SetBlocked, SetInput, SetLocked, SetSwitch } from '../SettingsShell/controls';
import { EscalationRuleModal, TransferRuleModal } from './RuleModals';
import { GmailConnectCard } from './GmailConnectCard';
import { ReceptionistOwnCards } from './ReceptionistOwnCards';
import { GMAIL_ACCOUNT, useGmailStore } from '../../settings-gmail/store';
import { CAPABILITIES, EMAIL_ESCALATIONS, SMS_ESCALATIONS, TRANSFER_RULES, VERIFIERS } from '../../settings-channels/mockData';
import { CAPABILITY_GROUPS } from '../../settings-channels/types';
import type { Capability, Channel, EscalationRule, TransferRule, Verifier } from '../../settings-channels/types';
import { PHONE } from '../../settings-phone/mockData';
import { REGISTRATION_WORD } from '../../settings-phone/types';
import type { TextingRegistration } from '../../settings-phone/types';

/**
 * One store, shown per channel (§9.4). It lives outside the component because
 * the three channel routes share one mounted instance, and because the card's
 * own claim — changing a switch here changes it on the other two — has to be
 * true rather than three copies that can drift. Escalation rules are the one
 * thing that is genuinely per-channel.
 */
const channelStore: {
  caps: Capability[];
  verifiers: Verifier[];
  transfers: TransferRule[];
  escalations: Record<'sms' | 'email', EscalationRule[]>;
} = {
  caps: CAPABILITIES,
  verifiers: VERIFIERS,
  transfers: TRANSFER_RULES,
  escalations: { sms: SMS_ESCALATIONS, email: EMAIL_ESCALATIONS },
};

export interface ChannelViewProps {
  channel: Channel;
  location: string;
  /**
   * The location's A2P 10DLC status (spec 16 §2.2). Defaults to what Phone
   * numbers shows, so the two pages cannot disagree — the whole point of the
   * change is that one screen says why the other one is lying.
   */
  texting?: TextingRegistration;
}

const TITLES: Record<Channel, { lead: string; accent: string; sub: React.ReactNode }> = {
  call: {
    lead: 'How ELVA',
    accent: 'calls',
    // Spec 39: this one page is everything about calls — the Call channel cards and
    // the AI Receptionist's own cards together.
    sub: (
      <>
        <strong className="text-[var(--text-secondary)]">ELVA answers every call, at every hour.</strong> Voice and
        personality are set on <strong className="text-[var(--text-secondary)]">General</strong>.
      </>
    ),
  },
  sms: {
    lead: 'How ELVA',
    accent: 'texts',
    sub: (
      <>What ELVA can do by text, how it checks who it is talking to, and when it pulls a person in.</>
    ),
  },
  email: {
    lead: 'How ELVA',
    accent: 'emails',
    sub: (
      <>What ELVA can do by email, how it checks who it is talking to, and when it raises a message to staff.</>
    ),
  },
};

const EYEBROW: Record<Channel, string> = {
  call: 'AI Brain · Channels · Call',
  sms: 'AI Brain · Channels · SMS',
  email: 'AI Brain · Channels · Email',
};

/**
 * One shape, three instances (spec 15 §9.4). Five cards shared, plus a sixth
 * that differs: SMS gets the texting mechanics, email gets the outbound
 * template, and call has no card 06.
 */
export function ChannelView({ channel, location, texting = PHONE.texting }: ChannelViewProps) {
  /**
   * **`Off` is a choice. `Blocked` is a fact. They must not look the same** (§2.2).
   *
   * While the texting registration is pending or rejected, carriers reject every
   * message from this number — not bounce, reject. Without this the SMS page
   * would look perfectly configured, every switch on, and not one text would
   * arrive; nothing else in the product would say why.
   */
  const smsBlocked = channel === 'sms' && texting.status !== 'approved';
  const blockedReason =
    `Texting registration is ${REGISTRATION_WORD[texting.status].toLowerCase()}. ` +
    'Carriers reject every message from an unregistered number, so nothing on this page can act.';
  // The three channel routes render this same component at the same position, so
  // React swaps the prop without remounting and a `useState` initialiser would
  // never re-run — which would show one channel's escalation rules on another.
  // The capability matrix and the verifiers are deliberately ONE store, so that
  // the card's claim — changing a switch here changes it on the other two — is
  // literally true rather than three copies that drift.
  const [, force] = useReducer((n: number) => n + 1, 0);
  const caps = channelStore.caps;
  const verifiers = channelStore.verifiers;
  const transfers = channelStore.transfers;
  const escalations = channel === 'call' ? [] : channelStore.escalations[channel];

  const [addingTransfer, setAddingTransfer] = useState(false);
  const [addingEscalation, setAddingEscalation] = useState(false);

  const setCaps = (next: Capability[]) => {
    channelStore.caps = next;
    force();
  };
  const setVerifiers = (next: Verifier[]) => {
    channelStore.verifiers = next;
    force();
  };
  const setTransfers = (next: TransferRule[]) => {
    channelStore.transfers = next;
    force();
  };
  const setEscalations = (next: EscalationRule[]) => {
    if (channel === 'call') return;
    channelStore.escalations[channel] = next;
    force();
  };

  // Derived from the matrix (§3.1).
  const onCount = caps.filter((c) => c.on[channel]).length;
  const verifiersOn = verifiers.filter((v) => v.on[channel]).length;
  const channelNoun = channel === 'call' ? 'calls' : channel === 'sms' ? 'texts' : 'email';
  const t = TITLES[channel];

  return (
    <div>
      <SettingsPageHeader eyebrow={EYEBROW[channel]} titleLead={t.lead} titleAccent={t.accent} sub={t.sub} />

      {/* The banner, in the same wording as the Phone numbers row (§2.2). It is
          at the top because everything below it is inert while it is showing. */}
      {smsBlocked ? (
        <div className="mb-6 flex items-start gap-3 rounded-[14px] border border-[#E8C98A] bg-[#FBF3E4] px-5 py-4">
          <Icon kind="alert" size={17} stroke={2.2} className="mt-0.5 shrink-0 text-[var(--warn-amber)]" />
          <div className="min-w-0 text-[12.5px] leading-[1.65] text-[var(--warn-amber)]">
            <div className="text-[13.5px] font-bold">
              Texting registration is {REGISTRATION_WORD[texting.status].toLowerCase()} — no text from this number
              reaches anybody
            </div>
            <p className="m-0 mt-1">
              US carriers require every business number to be registered before it may send texts (
              <b className="font-semibold">A2P 10DLC</b>). While this is pending or rejected,{' '}
              <b className="font-semibold">carriers reject every message — not bounce, reject.</b> Every outbound
              capability below therefore reads as <b className="font-semibold">Blocked</b> rather than as off:{' '}
              <b className="font-semibold">off is a choice, blocked is a fact,</b> and they must not look the same.
              {texting.reason !== undefined ? <> The carrier gave the reason: {texting.reason}.</> : null}{' '}
              <a href="/settings/phone-numbers" className="font-semibold underline">
                Phone numbers
              </a>{' '}
              has the status.
            </p>
          </div>
        </div>
      ) : null}

      <SettingsCards>
        {/* 00 — the practice's Gmail inbox (spec 38). Email only; first on the page. */}
        {channel === 'email' ? <GmailConnectCard /> : null}

        {/* 01 — what ELVA can do here */}
        <SettingsCard
          n="01"
          title={channel === 'call' ? 'What ELVA can do on a call' : 'What ELVA can do here'}
          sub={
            smsBlocked ? (
              <>
                <strong className="text-[var(--warn-amber)]">
                  All {caps.length} actions are blocked, not off
                </strong>{' '}
                — {onCount} of them are switched on and would act the moment the texting registration is approved. The
                switches are shown as blocked so nobody &ldquo;fixes&rdquo; a page that is already configured correctly.
              </>
            ) : (
              <>
                <strong className="text-[var(--text-secondary)]">
                  {onCount} of {caps.length}
                </strong>{' '}
                actions are on for {channelNoun}. If an action is off, ELVA points the patient to a channel where
                it&rsquo;s on.
              </>
            )
          }
        >
          {CAPABILITY_GROUPS.map((g) => {
            const inGroup = caps.filter((c) => c.group === g.id);
            const groupOn = inGroup.filter((c) => c.on[channel]).length;
            return (
              <div key={g.id}>
                <div className="flex items-center justify-between gap-3 bg-[var(--surface-sunken)] px-6 py-2">
                  <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    {g.label}
                  </span>
                  <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">
                    {groupOn}/{inGroup.length}
                  </span>
                </div>
                {inGroup.map((c) => {
                  const on = c.on[channel];
                  return (
                    <SettingsRow
                      key={c.id}
                      muted={!on || smsBlocked}
                      label={c.label}
                      desc={
                        <>
                          {c.desc}
                          {/* While texting is unregistered the row's own reason is
                              not the reason it cannot act, so it is replaced. */}
                          {smsBlocked ? (
                            <>
                              {' '}
                              <strong className="text-[var(--warn-amber)]">
                                Blocked by the texting registration, not switched off — this would be on as soon as the
                                registration is approved.
                              </strong>
                            </>
                          ) : (
                            <>
                              {/* Every off switch carries its own reason (§9.4). */}
                              {!on && c.offReason ? (
                                <>
                                  {' '}
                                  <strong className="text-[var(--warn-amber)]">{c.offReason}</strong>
                                </>
                              ) : null}
                              {on && c.offReason ? (
                                <>
                                  {' '}
                                  <em>{c.offReason}</em>
                                </>
                              ) : null}
                            </>
                          )}
                        </>
                      }
                      control={
                        smsBlocked ? (
                          <SetBlocked reason={blockedReason} />
                        ) : (
                          <SetSwitch
                            label={c.label}
                            checked={on}
                            onChange={(next) => {
                              setCaps(
                                caps.map((x) => (x.id === c.id ? { ...x, on: { ...x.on, [channel]: next } } : x)),
                              );
                            }}
                          />
                        )
                      }
                    />
                  );
                })}
              </div>
            );
          })}
        </SettingsCard>

        {/* 02 — how ELVA checks who it is talking to */}
        <SettingsCard
          n="02"
          title="How ELVA checks who it is talking to"
          sub={
            <>
              <strong className="text-[var(--text-secondary)]">
                Nothing from the chart is shared until this check passes.
              </strong>
            </>
          }
        >
          {verifiers.map((v, i) => {
            const on = v.on[channel];
            return (
              <SettingsRow
                key={v.id}
                last={i === verifiers.length - 1}
                muted={!on}
                label={v.label[channel]}
                control={
                  <SetSwitch
                    label={v.label[channel]}
                    checked={on}
                    onChange={(next) => {
                      setVerifiers(
                        verifiers.map((x) => (x.id === v.id ? { ...x, on: { ...x.on, [channel]: next } } : x)),
                      );
                    }}
                  />
                }
              />
            );
          })}
          <SettingsNote tone="indigo">
            <strong className="text-[var(--text-secondary)]">A name, plus at least one of these.</strong> {verifiersOn}{' '}
            on for {channelNoun}.{' '}
            <strong className="text-[var(--text-secondary)]">
              A matching {channel === 'email' ? 'address' : 'phone number'} alone opens the appointment, not the chart.
            </strong>
          </SettingsNote>
        </SettingsCard>

        {/* 03 — escalation: three different mechanics */}
        {channel === 'call' ? (
          <SettingsCard
            n="03"
            title="When ELVA hands the call to a person"
            sub={
              <>
                ELVA tells whoever picks up what the patient asked and why, then connects them. Each rule needs a
                number.
              </>
            }
          >
            {transfers.map((r, i) => (
              <div
                key={r.id}
                className="flex flex-wrap items-center gap-4 px-6 py-3.5"
                style={{ borderTop: '1px solid #edf0f6' }}
              >
                <span className="font-[var(--font-mono)] text-[11.5px] text-[var(--text-muted)]">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="min-w-0 flex-1 text-[13.5px] text-[var(--text-primary)]">{r.condition}</span>
                <span className="font-[var(--font-mono)] text-[12.5px] text-[var(--text-secondary)]">
                  {r.destination}
                </span>
                <button
                  type="button"
                  onClick={() => { setTransfers(transfers.filter((x) => x.id !== r.id)); }}
                  className="text-[12px] text-[var(--text-muted)] hover:underline"
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="px-6 py-4">
              <button
                type="button"
                onClick={() => { setAddingTransfer(true); }}
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-[var(--border-default)] px-3 py-2 text-[13px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--surface-sunken)]"
              >
                <Icon kind="plus" size={12} stroke={2.4} /> Add a transfer rule
              </button>
            </div>
          </SettingsCard>
        ) : (
          <SettingsCard
            n="03"
            title={channel === 'sms' ? 'When ELVA pulls a person into a text thread' : 'When ELVA raises an email to staff'}
            sub={
              channel === 'sms' ? (
                <>ELVA keeps replying and raises a Triage note.</>
              ) : (
                <>ELVA raises a Triage note.</>
              )
            }
          >
            {escalations.map((r, i) => (
              <div
                key={r.id}
                className="flex flex-wrap items-center gap-4 px-6 py-3.5"
                style={{ borderTop: '1px solid #edf0f6' }}
              >
                <span className="font-[var(--font-mono)] text-[11.5px] text-[var(--text-muted)]">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="min-w-0 flex-1 text-[13.5px] text-[var(--text-primary)]">{r.condition}</span>
                <span className="text-[11.5px] text-[var(--text-muted)]">Triage note</span>
                <button
                  type="button"
                  onClick={() => { setEscalations(escalations.filter((x) => x.id !== r.id)); }}
                  className="text-[12px] text-[var(--text-muted)] hover:underline"
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="px-6 py-4">
              <button
                type="button"
                onClick={() => { setAddingEscalation(true); }}
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-[var(--border-default)] px-3 py-2 text-[13px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--surface-sunken)]"
              >
                <Icon kind="plus" size={12} stroke={2.4} /> Add an escalation rule
              </button>
            </div>
          </SettingsCard>
        )}

        {/* On the call page, the AI Receptionist's own cards sit here — 04–07 (spec 39). */}
        {channel === 'call' ? <ReceptionistOwnCards ns={['04', '05', '06', '07', '08']} /> : null}

        {/* identity and sending — 04 on sms/email, 08 on the merged call page */}
        <SettingsCard
          n={channel === 'call' ? '08' : '04'}
          title="Identity and sending"
          sub={<>The numbers ELVA uses. They&rsquo;re set in the Account app.</>}
        >
          <IdentityRows channel={channel} />
        </SettingsCard>

        {/* 05 — quiet hours */}
        {channel === 'email' ? (
          <SettingsCard n="05" accent="green" title="Quiet hours — none on email">
            <div className="px-6 py-5 text-[12.5px] leading-[1.65] text-[var(--text-secondary)]">
              Emails are sent at any hour.
            </div>
          </SettingsCard>
        ) : (
          <SettingsCard
            n={channel === 'call' ? '09' : '05'}
            title={channel === 'call' ? 'Call hours' : 'Quiet hours'}
            sub={<>Applies to every agent.</>}
          >
            <SettingsRow
              label={`ELVA may reach a patient on ${channel} between`}
              desc={
                <>
                  In this location&rsquo;s time zone. A {channel === 'call' ? 'call' : 'text'} outside these hours waits
                  for the next window.
                </>
              }
              control={
                <span className="flex items-center gap-2">
                  <SetInput ariaLabel="Quiet hours from" className="w-[100px]" value={channel === 'call' ? '09:00' : '08:00'} />
                  <span className="text-[12.5px] text-[var(--text-muted)]">to</span>
                  <SetInput ariaLabel="Quiet hours to" className="w-[100px]" value={channel === 'call' ? '18:00' : '20:00'} />
                </span>
              }
            />
            {/* The exception, which is the thing each channel must get right. */}
            <SettingsRow
              last
              label={
                channel === 'call'
                  ? 'ELVA answers every inbound call, at any hour'
                  : 'ELVA replies to a patient who texted first, at any hour'
              }
              desc={
                channel === 'call' ? (
                  <>Only for calls ELVA places. Patients who call are always answered.</>
                ) : (
                  <>Only for texts ELVA starts. A patient who writes is answered at any hour.</>
                )
              }
              control={<SetLocked />}
            />
          </SettingsCard>
        )}

        {/* 06 — differs by channel; call has none */}
        {channel === 'sms' ? <SmsRulesCard /> : null}
        {channel === 'email' ? <EmailTemplateCard /> : null}
      </SettingsCards>

      <SettingsFooter location={location} />

      <TransferRuleModal
        open={addingTransfer}
        onClose={() => { setAddingTransfer(false); }}
        onAdd={(r) => {
          setTransfers([...transfers, r]);
          setAddingTransfer(false);
        }}
      />
      <EscalationRuleModal
        open={addingEscalation}
        channel={channel === 'email' ? 'email' : 'sms'}
        onClose={() => { setAddingEscalation(false); }}
        onAdd={(r) => {
          setEscalations([...escalations, r]);
          setAddingEscalation(false);
        }}
      />
    </div>
  );
}

function IdentityRows({ channel }: { channel: Channel }) {
  if (channel === 'call') {
    return (
      <>
        <Sourced label="Inbound number" desc="What patients call. Assigned by the Account app, not chosen here." value="(210) 555–0140" />
        <Sourced
          label="Outbound caller ID"
          desc="What a patient sees when ELVA calls them."
          value="(210) 555–0140"
        />
        <Sourced
          last
          label="Invis-Line"
          desc="The Invisalign Coordinator’s own inbound line. Shown only while that agent is on."
          value="(210) 555–0177"
        />
      </>
    );
  }
  if (channel === 'sms') {
    return (
      <>
        <Sourced label="Texting number" desc="The same number as calls. Set in the Account app." value="(210) 555–0140" />
        <SettingsRow
          label="Signature"
          desc="Appended to the first text in a thread, not to every message."
          control={<SetInput ariaLabel="Signature" className="w-[240px]" value="— Alamo Heights Dental" />}
        />
        <SettingsRow
          last
          label="Opt-out footer"
          desc={
            <>
              <strong className="text-[var(--text-secondary)]">Always on.</strong>
            </>
          }
          control={
            <span className="flex items-center gap-3">
              <span className="text-[12.5px] text-[var(--text-secondary)]">Reply STOP to opt out</span>
              <SetLocked />
            </span>
          }
        />
      </>
    );
  }
  return (
    <>
      {/* Two senders now (spec 38 §3): AI Email's replies go through the practice's
          Gmail; every other agent email is sent by ELVA (SES). */}
      <EmailReplySenderRow />
      <SettingsRow
        label="Reminders and other agent emails"
        desc="Reminders, recalls, the Invisalign programme and balance follow-ups. ELVA sends these itself."
        control={
          <span className="flex items-center gap-2">
            <span className="text-[12px] text-[var(--text-muted)]">Sent by ELVA from</span>
            <SetInput ariaLabel="From address" className="w-[260px]" value="hello@alamoheightsdental.com" />
          </span>
        }
      />
      <SettingsRow label="Signature" desc="Multi-line. Practice name, phone and web address.">
        <div className="mt-2 rounded-[10px] bg-[var(--surface-sunken)] px-3.5 py-3 text-[12.5px] leading-[1.7] text-[var(--text-secondary)]">
          Alamo Heights Dental
          <br />
          (210) 555–0140
          <br />
          alamoheightsdental.com
        </div>
      </SettingsRow>
      <SettingsRow
        label="Target response time"
        desc="What ELVA tells a patient to expect, and what the Triage queue is measured against."
        control={<SetInput ariaLabel="Target response time" className="w-[160px]" value="Within 1 hour" />}
      />
      <SettingsRow
        last
        label="Acknowledge a new email automatically"
        desc="Within 30 seconds."
        control={<SetSwitch label="Acknowledge a new email automatically" checked onChange={() => undefined} />}
      />
    </>
  );
}

function Sourced({
  label,
  desc,
  value,
  last,
}: {
  label: string;
  desc: string;
  value: string;
  last?: boolean;
}) {
  return (
    <SettingsRow
      {...(last ? { last: true } : {})}
      label={label}
      desc={desc}
      control={
        <span className="block text-right">
          <span className="block font-[var(--font-mono)] text-[13.5px] font-semibold text-[var(--text-primary)]">
            {value}
          </span>
          <span className="mt-0.5 block text-[10.5px] text-[var(--text-muted)]">From Account · loc_003</span>
        </span>
      }
    />
  );
}

/** Card 04, email — the AI Email reply sender, read-only and following card 00 (§3). */
function EmailReplySenderRow() {
  const { state } = useGmailStore();
  const connected = state === 'connected';
  return (
    <SettingsRow
      label="Replies to patients’ emails"
      desc="AI Email answers from the practice’s Gmail."
      control={
        <span className="block text-right">
          {connected ? (
            <>
              <span className="block text-[12.5px] text-[var(--text-secondary)]">
                From your Gmail: <span className="font-[var(--font-mono)] font-semibold text-[var(--text-primary)]">{GMAIL_ACCOUNT.address}</span>
              </span>
              <a href="#gmail" className="mt-0.5 inline-block text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">Change in card 00</a>
            </>
          ) : (
            <a href="#gmail" className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline">Connect Gmail in card 00</a>
          )}
        </span>
      }
    />
  );
}

/** SMS card 06 — the mechanics of texting, not what ELVA says. */
function SmsRulesCard() {
  const [oneThread, setOneThread] = useState(true);
  const [shortenLinks, setShortenLinks] = useState(true);
  const [images, setImages] = useState(false);
  return (
    <SettingsCard
      n="06"
      title="Rules every agent follows when it texts"
      sub={<>How texts are sent.</>}
    >
      <SettingsRow
        label="Name the practice in the first message of a thread"
        desc={
          <>
            <strong className="text-[var(--text-secondary)]">Always on.</strong>
          </>
        }
        control={<SetLocked />}
      />
      <SettingsRow
        label="Keep one thread per patient"
        desc={<>ELVA replies in the existing conversation.</>}
        control={<SetSwitch label="Keep one thread per patient" checked={oneThread} onChange={setOneThread} />}
      />
      <SettingsRow
        label="Longest message ELVA will send"
        desc="Longer messages are cut to a line and a link."
        control={
          <span className="flex items-center gap-2.5">
            <SetInput ariaLabel="Longest message" mono align="right" suffix="segments" className="w-[138px]" value="3" />
            <span className="text-[12.5px] text-[var(--text-muted)]">about 480 characters</span>
          </span>
        }
      />
      <SettingsRow
        label="Shorten links on the practice’s own domain"
        desc={
          <>
            Links go out as <strong className="text-[var(--text-secondary)]">alamoheights.elva.ai/…</strong>
          </>
        }
        control={<SetSwitch label="Shorten links" checked={shortenLinks} onChange={setShortenLinks} />}
      />
      <SettingsRow
        label="Send images and attachments"
        desc="Off. ELVA doesn’t send pictures."
        control={<SetSwitch label="Send images and attachments" checked={images} onChange={setImages} />}
      />
      <SettingsRow
        last
        label="Handle STOP and HELP"
        desc={
          <>
            <strong className="text-[var(--text-secondary)]">Locked.</strong> STOP stops texts from this number to that
            patient, from every agent, until they text START. Email and calls aren’t affected. HELP replies with the
            practice name and number.
          </>
        }
        control={<SetLocked />}
      />
    </SettingsCard>
  );
}

/** Email card 06 — one template, every agent. */
function EmailTemplateCard() {
  return (
    <SettingsCard
      n="06"
      title="The outbound email template"
      sub={<>Every email ELVA sends uses this template.</>}
    >
      <div className="px-6 py-5">
        <div className="overflow-hidden rounded-[12px] border border-[var(--border-default)]">
          <div className="bg-[var(--brand-indigo)] px-5 py-4 text-[16px] font-bold text-white">
            Alamo Heights Dental
          </div>
          <div className="px-5 py-4 text-[13px] leading-[1.7] text-[var(--text-primary)]">
            <div className="text-[15px] font-bold">Your appointment on Thursday</div>
            <p className="m-0 mt-2">Hi Maria,</p>
            <p className="m-0 mt-2">
              You are booked with Dr. Saito on{' '}
              <strong>Thursday 2 October at 2:30pm</strong>. Please arrive ten minutes early if you have not sent your
              forms back.
            </p>
            <span className="mt-3 inline-block rounded-[8px] bg-[var(--brand-indigo)] px-3.5 py-2 text-[13px] font-semibold text-white">
              Confirm this appointment
            </span>
          </div>
          <div className="bg-[var(--surface-sunken)] px-5 py-3 text-[11.5px] leading-[1.7] text-[var(--text-muted)]">
            Alamo Heights Dental · 4400 Broadway St, San Antonio, TX 78209
            <br />
            (210) 555–0140 · alamoheightsdental.com
            <br />
            <span className="text-[var(--text-secondary)] underline">Unsubscribe from reminders like this</span>
          </div>
        </div>
      </div>

      <SettingsRow
        label="Logo"
        desc={
          <>
            Shown in the header. Falls back to the practice name set on{' '}
            <strong className="text-[var(--text-secondary)]">Practice management</strong> when there is none.
          </>
        }
        control={
          <span className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-[9px] bg-[var(--brand-indigo-tint)] text-[12px] font-bold text-[var(--brand-indigo)]">
              AH
            </span>
            <button type="button" className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
              Replace →
            </button>
          </span>
        }
      />
      <SettingsRow
        label="Header colour"
        desc={
          <>
            <strong className="text-[var(--text-secondary)]">The brand colour from General.</strong>
          </>
        }
        control={
          <span className="flex items-center gap-3">
            <span className="h-8 w-8 rounded-[9px] bg-[var(--brand-indigo)]" aria-label="Brand colour" />
            <button type="button" className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
              Change on General →
            </button>
          </span>
        }
      />
      <SettingsRow
        label="Footer"
        desc={
          <>
            Address, phone and web address, from{' '}
            <strong className="text-[var(--text-secondary)]">Practice management</strong>.
          </>
        }
        control={
          <span className="block text-right">
            <span className="block font-[var(--font-mono)] text-[13px] font-semibold text-[var(--text-secondary)]">
              From Practice management
            </span>
            <span className="mt-0.5 block text-[10.5px] text-[var(--text-muted)]">3 lines</span>
          </span>
        }
      />
      <SettingsRow
        last
        label="Unsubscribe line"
        desc={
          <>
            <strong className="text-[var(--text-secondary)]">Always on.</strong> It stops that kind of email only.
          </>
        }
        control={<SetLocked />}
      />
    </SettingsCard>
  );
}
